import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCcw, ArrowDown } from "lucide-react";

/**
 * PullToRefresh — native-feel pull-to-refresh gesture for mobile (<1024px only).
 * Wrap page content. Calls `onRefresh` (async) when user pulls past threshold and releases.
 *
 * Behavior:
 * - Only active on viewport width < 1024px.
 * - Only triggers when window is scrolled to top at touchstart.
 * - Follows finger with resistance (dy * 0.5).
 * - Threshold: 70px.
 * - Ignores gestures locked to horizontal (drawer swipe etc).
 */
export default function PullToRefresh({ onRefresh, children }) {
  const [pull, setPull] = useState(0); // current pull distance in px
  const [refreshing, setRefreshing] = useState(false);
  const [animating, setAnimating] = useState(false); // controls transition on release
  const startRef = useRef(null); // { x, y, active, locked }

  const THRESHOLD = 70;
  const MAX = 120;

  const isMobile = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

  const onTouchStart = useCallback((e) => {
    if (!isMobile() || refreshing) return;
    if (window.scrollY > 0) return;
    const t = e.touches[0];
    startRef.current = { x: t.clientX, y: t.clientY, active: true, locked: null };
    setAnimating(false);
  }, [refreshing]);

  const onTouchMove = useCallback((e) => {
    const s = startRef.current;
    if (!s || !s.active) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (!s.locked) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      s.locked = Math.abs(dy) > Math.abs(dx) ? "v" : "h";
    }
    if (s.locked !== "v" || dy <= 0) {
      setPull(0);
      return;
    }
    // Apply resistance
    const resisted = Math.min(MAX, dy * 0.5);
    setPull(resisted);
  }, []);

  const onTouchEnd = useCallback(async () => {
    const s = startRef.current;
    if (!s || !s.active) return;
    s.active = false;
    const distance = pull;
    setAnimating(true);
    if (distance >= THRESHOLD && typeof onRefresh === "function") {
      setRefreshing(true);
      setPull(48);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
  }, [pull, onRefresh]);

  useEffect(() => {
    const opts = { passive: true };
    window.addEventListener("touchstart", onTouchStart, opts);
    window.addEventListener("touchmove", onTouchMove, opts);
    window.addEventListener("touchend", onTouchEnd, opts);
    window.addEventListener("touchcancel", onTouchEnd, opts);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onTouchStart, onTouchMove, onTouchEnd]);

  const ready = pull >= THRESHOLD;
  const showIndicator = pull > 0 || refreshing;

  let indicatorLabel;
  if (refreshing) indicatorLabel = "Memuat…";
  else if (ready) indicatorLabel = "Lepas untuk refresh";
  else indicatorLabel = "Tarik untuk refresh";

  return (
    <div className="relative">
      {/* Indicator */}
      <div
        data-testid="ptr-indicator"
        aria-hidden={!showIndicator}
        className="lg:hidden pointer-events-none absolute inset-x-0 top-0 flex items-start justify-center overflow-hidden"
        style={{
          height: `${pull}px`,
          transition: animating ? "height 250ms ease-out" : "none",
        }}
      >
        <div className="mt-2 flex flex-col items-center gap-1 text-[#0A0A0A]">
          {refreshing ? (
            <RefreshCcw className="h-5 w-5 animate-spin" />
          ) : (
            <ArrowDown
              className={`h-5 w-5 transition-transform duration-200 ${
                ready ? "rotate-180 text-[#0055FF]" : "rotate-0 text-[#0A0A0A]/60"
              }`}
            />
          )}
          <span
            data-testid="ptr-label"
            className={`text-[10px] font-bold uppercase tracking-widest ${
              ready ? "text-[#0055FF]" : "text-[#0A0A0A]/60"
            }`}
          >
            {indicatorLabel}
          </span>
        </div>
      </div>

      {/* Content that translates with the pull */}
      <div
        style={{
          transform: `translateY(${pull}px)`,
          transition: animating ? "transform 250ms ease-out" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
